import { FormDivider } from '@hex/calculators-shared/components';
import { ChartLegend } from '@hex/calculators-shared/components/DonutChart/ChartLegend';
import { Donut } from '@hex/calculators-shared/components/DonutChart/Donut';
import { ResultWhiteBox } from '@hex/calculators-shared/layout';
import { Flex, Typography, tokens } from '@sapphire-react/core';
import { Fragment } from 'react/jsx-runtime';
import { useTranslation } from 'react-i18next';
import { useCostCalculator } from '../../../hooks/useCostCalculator';
import { LoanCalculatorForm } from '../../../types';
import { formatNumber } from '../../../utils';
import { Footer } from './Footer';

type Props = {
  data?: LoanCalculatorForm;
};

export const DonutAndNumbers = ({ data }: Props) => {
  const { t } = useTranslation();
  const costCalculatorResult = useCostCalculator(data);

  if (!data || !costCalculatorResult) return null;

  const { firstInterest, insurancePremiumValue, amortization, monthlyInstallment } = costCalculatorResult;

  const loanInsurancePremium = data.loanRepaymentSecurity
    ? [
        {
          value: insurancePremiumValue,
          legendTitle: t('results.donutChart.firstInsurancePremium.label'),
          testId: 'first-insurance-premium-value',
          borderRadius: 0,
          color: tokens.color.backgroundDecorative7.value,
        },
      ]
    : [];

  const chartItems = [
    {
      value: amortization,
      color: tokens.color.backgroundDecorative1.value,
      legendTitle: t('results.donutChart.amortization.label'),
      borderRadius: 0,
      contextualHelpContent: (
        <Typography.Body size={'sm'}>{t('results.donutChart.amortization.tooltip')}</Typography.Body>
      ),
      testId: 'amortization-value',
    },
    {
      value: firstInterest,
      color: tokens.color.backgroundDecorative3.value,
      legendTitle: t('results.donutChart.interestPayment.label'),
      borderRadius: 0,
      testId: 'interest-payment-value',
    },
    ...loanInsurancePremium,
  ];

  const loanDetails = [
    {
      value: `${formatNumber(data.loanAmount)} €`,
      legendTitle: t('results.donutChart.loanAmount.label'),
      testId: 'loan-amount-value',
    },
    {
      value: `${data.maturity} ${t('years')}`,
      legendTitle: t('results.donutChart.maturity.label'),
      testId: 'maturity-value',
      contextualHelpContent: (
        <Typography.Body size={'sm'}>{t('results.donutChart.maturity.tooltip.text')}</Typography.Body>
      ),
    },
    {
      value: `${formatNumber(data.interest || '0', true)} %`,
      legendTitle: t('results.donutChart.interestRate.label'),
      testId: 'interest-rate-value',
      contextualHelpContent: (
        <Typography.Body size={'sm'}>{t('results.donutChart.interestRate.tooltip.label')}</Typography.Body>
      ),
    },
  ];
  const total = chartItems.reduce((prev, curr) => prev + (curr.value ?? 0), 0);

  const segments = total
    ? chartItems.map((item) => ({
        percentage: ((item.value ?? 0) * 100) / total,
        color: item.color,
        label: item.legendTitle,
        formattedValue: `${formatNumber(item.value ?? 0)} €`,
      }))
    : [];
  const donutSize = 238;

  return (
    <ResultWhiteBox border={'none'} borderRadius={tokens.size.radiusSm.value} addBottomMargin={false}>
      <Donut
        segments={segments}
        donutHeight={donutSize}
        donutWidth={donutSize}
        circleSizeX={donutSize}
        circleSizeY={donutSize}
        strokeWidth={2.6}
        interactive
      >
        <>
          <Typography.Heading data-test-id={'donut-total'} level={4}>
            {`${formatNumber(monthlyInstallment)} €`}
          </Typography.Heading>
          <Typography.Body
            size={'sm'}
            isBold
            UNSAFE_style={{
              color: tokens.color.foregroundSecondary.value,
              fontWeight: 400,
            }}
          >
            {t('results.donutChart.monthlyInstallment')}
          </Typography.Body>
        </>
      </Donut>
      <Flex
        flexDirection={'column'}
        width={'100%'}
        paddingLeft={{ base: tokens.size.spacingMd, sm: tokens.size.spacingLg }}
        paddingRight={{ base: tokens.size.spacingMd, sm: tokens.size.spacingLg }}
        UNSAFE_style={{ boxSizing: 'border-box' }}
      >
        {chartItems.map((item, index) => (
          <Fragment key={`legend-${index}`}>
            <ChartLegend chartItem={item} currency={'€'} />
          </Fragment>
        ))}
        <FormDivider marginBottom={tokens.size.spacingXs} marginTop={tokens.size.spacingXs.value} />
        {loanDetails.map((item, index) => (
          <Fragment key={`legend-${index}`}>
            <Footer chartItem={item} />
          </Fragment>
        ))}
      </Flex>
    </ResultWhiteBox>
  );
};
